import React from 'react';
import {
  Box,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, LocationOn, DateRange } from '@mui/icons-material';
import { inputStyle } from '../constants';

const StepDisponibilite = ({ formData, setFormData, today }) => {
  return (
    <Fade in>
      <Stack spacing={4}>
        {/* SECTION 1 : PÉRIODE DE RÉSERVATION */}
        <Box
          sx={{
            p: 3,
            borderRadius: '20px',
            bgcolor: '#F8FAFC',
            border: '1px solid #E2E8F0',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <DateRange sx={{ color: '#4318FF', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight="800" color="#1B2559">
              Période de réservation
            </Typography>
          </Box>
          <Typography variant="body2" color="#718096" mb={3}>
            Définissez les dates limites durant lesquelles ce soin individuel sera proposé à la réservation.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date de début"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today }}
                value={formData.date_debut_dispo || ''}
                onChange={(e) => setFormData({ ...formData, date_debut_dispo: e.target.value })}
                sx={inputStyle}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date de fin"
                InputLabelProps={{ shrink: true }}
                value={formData.date_fin_dispo || ''}
                onChange={(e) => setFormData({ ...formData, date_fin_dispo: e.target.value })}
                sx={inputStyle}
              />
            </Grid>
          </Grid>

          {/* Message de confirmation des dates */}
          {formData.date_debut_dispo && formData.date_fin_dispo && (
            <Fade in>
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: '#E6FFFA',
                  borderRadius: '12px',
                  border: '1px solid #05CD99',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <CheckCircle sx={{ color: '#05CD99', fontSize: 20 }} />
                <Typography variant="caption" color="#065F46" fontWeight="700">
                  La disponibilité est activée directement depuis le soin :
                  <span style={{ color: '#4318FF', marginLeft: '5px' }}>
                    {formData.titre_offre || 'Sans titre'}
                  </span>
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>

        {/* SECTION 2 : FORMAT & LOCALISATION */}
        <Box
          sx={{
            p: 3,
            borderRadius: '20px',
            bgcolor: '#F8FAFC',
            border: '1px solid #E2E8F0',
          }}
        >
          <Box display="flex" alignItems="center" justify="space-between" mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_deplacement_possible || false}
                  onChange={(e) =>
                    setFormData({ ...formData, is_deplacement_possible: e.target.checked })
                  }
                  color="primary"
                />
              }
              label={
                <Typography variant="subtitle1" fontWeight="800" color="#1B2559">
                  Déplacement possible (chez le client)
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <FormControl fullWidth sx={{ ...inputStyle, mb: 3 }}>
            <InputLabel>Format du soin</InputLabel>
            <Select
              value={formData.format_offre || ''}
              label="Format du soin"
              onChange={(e) => setFormData({ ...formData, format_offre: e.target.value })}
            >
              <MenuItem value="presentiel">Présentiel</MenuItem>
              <MenuItem value="zoom">En ligne (Zoom)</MenuItem>
              <MenuItem value="hybride">Hybride</MenuItem>
            </Select>
          </FormControl>

          {/* BLOC LOCALISATION CONDITIONNEL */}
          {(formData.format_offre === 'presentiel' || formData.format_offre === 'hybride') && (
            <Fade in>
              <Box sx={{ mt: 3, p: 3, bgcolor: '#fff', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <LocationOn sx={{ color: '#4318FF', fontSize: 22 }} />
                  <Typography variant="subtitle2" fontWeight="800" color="#1B2559">
                    Localisation du soin individuel
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, fontStyle: 'italic' }}>
                  * Si le soin s'effectue dans les locaux SOA, veuillez renseigner l'adresse du lieu SOA.
                </Typography>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Adresse"
                      value={formData.address_ws || ''}
                      onChange={(e) => setFormData({ ...formData, address_ws: e.target.value })}
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ville"
                      value={formData.city_ws || ''}
                      onChange={(e) => setFormData({ ...formData, city_ws: e.target.value })}
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Code postal"
                      value={formData.postal_code_ws || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, postal_code_ws: e.target.value })
                      }
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Pays"
                      value={formData.country_ws || ''}
                      onChange={(e) => setFormData({ ...formData, country_ws: e.target.value })}
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      value={formData.lat_ws || ''}
                      onChange={(e) => setFormData({ ...formData, lat_ws: e.target.value })}
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      value={formData.lng_ws || ''}
                      onChange={(e) => setFormData({ ...formData, lng_ws: e.target.value })}
                      sx={inputStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Note de localisation (optionnel)"
                      multiline
                      rows={2}
                      value={formData.location_note_ws || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, location_note_ws: e.target.value })
                      }
                      sx={inputStyle}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Fade>
          )}
        </Box>
      </Stack>
    </Fade>
  );
};

export default StepDisponibilite;